import { ReactNode } from "react";
import classNames from "classnames";
import { Dialog } from "@headlessui/react";
import { Button } from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { useHeadlessUIDialogBypass } from "@unleashed/hooks/useHeadlessUIDialogBypass";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";

interface ModalProps {
  children: ReactNode | ReactNode[];
  className?: string;
  variant?: "primary" | "danger" | "tertiary" | "pos" | "payment" | "left-pane";
  title?: string;
  onClose: () => void;
  fullSize?: boolean;
  size?: "md" | "lg" | "xl";
}

export const Modal = ({
  children,
  className,
  variant = "primary",
  title,
  onClose,
  fullSize,
  size = "md",
}: ModalProps) => {
  let color;
  switch (variant) {
  case "danger":
    color = "bg-danger border-danger";
    break;
  case "pos":
  case "payment":
    color = "bg-gray-50";
    break;
  case "left-pane":
    color = "bg-white border-l border-gray-200";
    break;
  default:
    color = "bg-unleashed-pink border-unleashed-pink";
    break;
  }

  let font;
  switch (variant) {
  case "pos":
  case "payment":
    font = "text-xl md:text-2xl font-unleashed";
    break;
  default:
    font = "text-white font-semibold text-lg";
    break;
  }

  useHeadlessUIDialogBypass();

  let bgColor;
  switch (variant) {
  case "pos":
  case "payment":
  case "left-pane":
    bgColor = "bg-gray-50";
    break;
  default:
    bgColor = "bg-white";
    break;
  }

  return (
    <Dialog
      open={true}
      onClose={() => {
        return;
      }}
      onClick={onClose}
      className="relative z-40"
    >
      <div
        className="fixed top-0 left-0 bottom-0 right-0 bg-black/30"
        aria-hidden="true"
      />
      <div
        className={classNames(
          "fixed",
          variant === "left-pane"
            ? "top-[56px] right-0 h-screen w-full md:w-[600px]"
            : classNames(
              "flex top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] max-h-[95vh] w-full md:px-0",
              size === "xl" ? "md:max-w-5xl" : size === "lg" ? "md:max-w-3xl" : "md:max-w-2xl"
            ),
          fullSize && variant !== "left-pane"
            ? "md:max-w-full md:max-h-full max-h-full h-full"
            : "lg:w-auto lg:max-w-screen-2xl",
          !["payment", "pos", "left-pane"].includes(variant) && "p-2"
        )}
      >
        <Dialog.Panel
          className={classNames(
            "flex flex-col relative max-h-screen w-full max-w-screen overflow-hidden border-2",
            color
          )}
        >
          <Dialog.Title
            className={classNames(
              "min-h-11 border-b-2 px-4 py-2 flex w-full items-center justify-between shadow-bt-md",
              variant === "left-pane"
                ? "bg-white text-gray-900 font-bold"
                : font
            )}
          >
            {!!title && (
              <span
                data-cy-modal-title={title}
                className={
                  ["payment", "pos"].includes(variant)
                    ? "w-full text-center"
                    : ""
                }
              >
                {title}
              </span>
            )}
            <Button
              variant="none"
              title={t`Close`}
              onClick={onClose}
              className={
                ["payment", "pos", "left-pane"].includes(variant)
                  ? "text-gray-500"
                  : "text-white"
              }
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </Dialog.Title>
          <div
            className={classNames(
              "overflow-auto",
              fullSize ? "h-full w-full" : "",
              bgColor
            )}
          >
            <div
              className={classNames(
                className,
                variant === "payment" ? "" : "p-6",
                fullSize ? "h-full w-full" : ""
              )}
            >
              {children}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
